import React from 'react';
import { useProduct } from 'src/api/inventory-management/products/useProduct';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { AttributeOptionsList } from './AttributeOptionsList';
import { useUpdateProductAttribute } from 'src/api/inventory-management/products/useUpdateProductAttribute';

interface ProductAttributesProps {
  productId: string;
  isExpanded: boolean;
}

const ProductAttributes: React.FC<ProductAttributesProps> = ({
  productId,
  isExpanded,
}) => {
  const { data: productData, isLoading } = useProduct(
    productId,
    {
      includes: ['attributes'],
    },
    isExpanded,
  );

  const product = productData?.product;

  if (isLoading) {
    return (
      <div className="flex justify-center p-3">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!product?.attributes?.length) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <p className="text-sm font-medium text-muted-foreground mb-1">
          No attributes defined for this product
        </p>
        <p className="text-xs text-muted-foreground">
          Add attributes to customize this product
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">Attributes</h3>
        <Badge variant="secondary" className="text-xs">
          {product.attributes.length}
        </Badge>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {product.attributes.map((attribute) => (
          <AccordionItem
            key={attribute.id}
            value={attribute.id}
            className="border rounded-lg px-4"
          >
            <AccordionTrigger className="hover:no-underline py-3">
              <div className="flex items-center gap-2 flex-1 text-left">
                <span className="font-medium text-sm">
                  {attribute.customField.fieldName}
                </span>
                <div className="flex items-center gap-1">
                  {attribute.isRequired && (
                    <Badge
                      variant="outline"
                      className="text-xs border-red-200 text-red-700 bg-red-50 dark:border-red-800 dark:text-red-300 dark:bg-red-950"
                    >
                      Required
                    </Badge>
                  )}
                  {attribute.defaultValue !== null &&
                    attribute.defaultValue !== undefined && (
                      <Badge variant="outline" className="text-xs">
                        Has default
                      </Badge>
                    )}
                  {attribute.customField.coreEnum && (
                    <Badge variant="secondary" className="text-xs">
                      {attribute.customField.coreEnum.options.length} options
                    </Badge>
                  )}
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-4 pt-2">
              <div className="space-y-4">
                {/* Required Toggle */}
                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                  <div className="flex flex-col gap-1">
                    <Label
                      htmlFor={`required-${attribute.id}`}
                      className="font-semibold text-sm cursor-pointer"
                    >
                      Required for Orders
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      When enabled, variants must have this attribute filled to
                      be included in orders
                    </p>
                  </div>
                  <RequiredToggle
                    productId={productId}
                    attributeId={attribute.id}
                    isRequired={attribute.isRequired}
                  />
                </div>

                {/* Description */}
                {attribute.customField.description && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold mb-1">
                      Description
                    </p>
                    <p className="text-sm text-foreground">
                      {attribute.customField.description}
                    </p>
                  </div>
                )}

                {/* Default Value (for non-enum fields) */}
                {!attribute.customField.coreEnum &&
                  attribute.defaultValue !== null &&
                  attribute.defaultValue !== undefined && (
                    <div>
                      <p className="text-xs text-muted-foreground uppercase font-semibold mb-1">
                        Default Value
                      </p>
                      <p className="text-sm text-foreground font-mono">
                        {String(attribute.defaultValue)}
                      </p>
                    </div>
                  )}

                {/* Options (for enum fields) */}
                {attribute.customField.coreEnum && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-semibold mb-2">
                      Options ({attribute.customField.coreEnum.options.length})
                    </p>
                    <AttributeOptionsList
                      options={attribute.customField.coreEnum.options}
                      defaultValue={attribute.defaultValue}
                      onToggleOption={(optionId, enabled) => {
                        // TODO: Implement toggle functionality
                        console.log('Toggle option:', optionId, enabled);
                      }}
                    />
                  </div>
                )}

                {/* Field Type Info */}
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <div>
                      <span className="font-semibold">Type:</span>{' '}
                      {attribute.customField.coreEnum ? 'Enum' : 'Custom'}
                    </div>
                    <div>
                      <span className="font-semibold">ID:</span>{' '}
                      <code className="bg-muted px-1 py-0.5 rounded text-[10px]">
                        {attribute.id}
                      </code>
                    </div>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

// Separate component for the toggle to avoid re-rendering the entire list
function RequiredToggle({
  productId,
  attributeId,
  isRequired,
}: {
  productId: string;
  attributeId: string;
  isRequired: boolean;
}) {
  const updateAttribute = useUpdateProductAttribute(productId, attributeId);

  function handleToggle(checked: boolean) {
    updateAttribute.mutate({ isRequired: checked });
  }

  return (
    <Switch
      id={`required-${attributeId}`}
      checked={isRequired}
      onCheckedChange={handleToggle}
      disabled={updateAttribute.isPending}
    />
  );
}

export default ProductAttributes;
